import React, { useCallback, useEffect, useRef, useState } from "react"
import {
  Autocomplete,
  GoogleMap,
  Marker,
  useJsApiLoader,
} from "@react-google-maps/api"
import { Box, HStack, Text, VStack } from "@chakra-ui/react"

const GOOGLE_MAPS_LIBRARIES: ["places"] = ["places"]
const RADIUS_OPTIONS = [1, 2, 5, 10]

export interface LocationEntry {
  id?: number
  location_name: string
  location_lat: number | null
  location_lng: number | null
  radius_miles: number
}

export const newLocation = (): LocationEntry => ({
  location_name: "",
  location_lat: null,
  location_lng: null,
  radius_miles: 2,
})

// ─── Single location row ──────────────────────────────────────────────────────

interface LocationRowProps {
  entry: LocationEntry
  index: number
  isLoaded: boolean
  canRemove: boolean
  onChange: (index: number, updated: Partial<LocationEntry>) => void
  onRemove: (index: number) => void
}

const LocationRow: React.FC<LocationRowProps> = ({
  entry,
  index,
  isLoaded,
  canRemove,
  onChange,
  onRemove,
}) => {
  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null)
  const [inputValue, setInputValue] = useState(entry.location_name)

  useEffect(() => {
    setInputValue(entry.location_name)
  }, [entry.location_name])

  const onLoad = useCallback(
    (ac: google.maps.places.Autocomplete) => setAutocomplete(ac),
    []
  )

  const onPlaceChanged = useCallback(() => {
    if (!autocomplete) return
    const place = autocomplete.getPlace()
    const lat = place.geometry?.location?.lat()
    const lng = place.geometry?.location?.lng()
    const name = place.formatted_address || place.name || ""
    if (lat != null && lng != null) {
      setInputValue(name)
      onChange(index, { location_name: name, location_lat: lat, location_lng: lng })
    }
  }, [autocomplete, index, onChange])

  const inputStyle: React.CSSProperties = {
    flex: 1,
    height: "36px",
    padding: "0 12px",
    borderRadius: "8px",
    border: "1px solid #CBD5E0",
    fontSize: "0.875rem",
    color: "#1A202C",
    outline: "none",
    boxSizing: "border-box",
    minWidth: 0,
  }

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="xl"
      p={4}
      position="relative"
    >
      <HStack justify="space-between" mb={3}>
        <HStack gap={2}>
          <Box
            w="22px"
            h="22px"
            borderRadius="full"
            bg="green.700"
            color="white"
            fontSize="xs"
            fontWeight="bold"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            {index + 1}
          </Box>
          <Text fontSize="sm" fontWeight="medium" color="gray.700">
            Location {index + 1}
          </Text>
        </HStack>
        {canRemove && (
          <Box
            as="button"
            type="button"
            onClick={() => onRemove(index)}
            fontSize="xs"
            color="gray.400"
            cursor="pointer"
            _hover={{ color: "red.500" }}
            lineHeight={1}
          >
            Remove
          </Box>
        )}
      </HStack>

      {isLoaded ? (
        <Autocomplete
          onLoad={onLoad}
          onPlaceChanged={onPlaceChanged}
          options={{ componentRestrictions: { country: "gb" } }}
          style={{ display: "block", width: "100%" }}
        >
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault() }}
            placeholder="Search for a location…"
            style={{ ...inputStyle, width: "100%" }}
          />
        </Autocomplete>
      ) : (
        <input type="text" placeholder="Loading..." disabled style={{ ...inputStyle, width: "100%" }} />
      )}

      <HStack gap={2} mt={3} align="center">
        <Text fontSize="xs" color="gray.500" flexShrink={0}>Radius:</Text>
        {RADIUS_OPTIONS.map((r) => {
          const selected = entry.radius_miles === r
          return (
            <Box
              key={r}
              as="button"
              type="button"
              onClick={() => onChange(index, { radius_miles: r })}
              px={3}
              py="3px"
              borderRadius="full"
              border="1px solid"
              borderColor={selected ? "green.600" : "gray.200"}
              bg={selected ? "green.700" : "white"}
              color={selected ? "white" : "gray.500"}
              fontWeight={selected ? "semibold" : "normal"}
              fontSize="xs"
              cursor="pointer"
              transition="all 0.15s"
              _hover={{ borderColor: "green.500", color: selected ? "white" : "green.700" }}
            >
              {r} mi
            </Box>
          )
        })}
      </HStack>
    </Box>
  )
}

// ─── Map options ──────────────────────────────────────────────────────────────

const MAP_OPTIONS: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  clickableIcons: false,
  styles: [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }],
}

// ─── Multi-location picker with side map ──────────────────────────────────────

interface MultiLocationPickerProps {
  locations: LocationEntry[]
  onChange: (locations: LocationEntry[]) => void
  mapHeight?: string
}

const MultiLocationPicker: React.FC<MultiLocationPickerProps> = ({
  locations,
  onChange,
  mapHeight = "360px",
}) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: GOOGLE_MAPS_LIBRARIES,
  })

  const [map, setMap] = useState<google.maps.Map | null>(null)
  const circlesRef = useRef<google.maps.Circle[]>([])

  useEffect(() => {
    if (!map) return

    circlesRef.current.forEach((c) => c.setMap(null))
    circlesRef.current = []

    const bounds = new google.maps.LatLngBounds()
    let hasAny = false

    locations.forEach((loc) => {
      if (loc.location_lat == null || loc.location_lng == null) return
      const center = { lat: loc.location_lat, lng: loc.location_lng }
      const radiusMetres = loc.radius_miles * 1609.34

      const circle = new google.maps.Circle({
        map,
        center,
        radius: radiusMetres,
        fillColor: "#16a34a",
        fillOpacity: 0.12,
        strokeColor: "#16a34a",
        strokeOpacity: 0.7,
        strokeWeight: 2,
      })
      circlesRef.current.push(circle)

      bounds.extend(new google.maps.LatLng(center.lat + (radiusMetres / 111320), center.lng))
      bounds.extend(new google.maps.LatLng(center.lat - (radiusMetres / 111320), center.lng))
      bounds.extend(new google.maps.LatLng(center.lat, center.lng + (radiusMetres / (111320 * Math.cos((center.lat * Math.PI) / 180)))))
      bounds.extend(new google.maps.LatLng(center.lat, center.lng - (radiusMetres / (111320 * Math.cos((center.lat * Math.PI) / 180)))))
      hasAny = true
    })

    if (hasAny) map.fitBounds(bounds, 24)

    return () => {
      circlesRef.current.forEach((c) => c.setMap(null))
      circlesRef.current = []
    }
  }, [map, locations])

  const handleEntryChange = useCallback(
    (index: number, updated: Partial<LocationEntry>) => {
      onChange(locations.map((loc, i) => (i === index ? { ...loc, ...updated } : loc)))
    },
    [locations, onChange]
  )

  const handleRemove = useCallback(
    (index: number) => onChange(locations.filter((_, i) => i !== index)),
    [locations, onChange]
  )

  const handleAdd = () => onChange([...locations, newLocation()])

  return (
    <Box display="flex" gap={5} alignItems="flex-start">
      {/* Left: location cards */}
      <VStack align="stretch" gap={3} flex="1" minW={0} maxW="380px">
        {locations.map((entry, i) => (
          <LocationRow
            key={i}
            index={i}
            entry={entry}
            isLoaded={isLoaded}
            canRemove={locations.length > 1}
            onChange={handleEntryChange}
            onRemove={handleRemove}
          />
        ))}

        <Box
          as="button"
          type="button"
          onClick={handleAdd}
          w="100%"
          py={3}
          borderRadius="xl"
          border="1.5px dashed"
          borderColor="green.300"
          bg="green.50"
          color="green.700"
          fontSize="sm"
          fontWeight="medium"
          cursor="pointer"
          transition="all 0.15s"
          _hover={{ bg: "green.100", borderColor: "green.500" }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={2}
        >
          <Box as="span" fontSize="lg" lineHeight={1} fontWeight="light">+</Box>
          Add another location
        </Box>
      </VStack>

      {/* Right: map */}
      <Box
        flex="1"
        minW={0}
        borderRadius="xl"
        overflow="hidden"
        border="1px solid"
        borderColor="gray.200"
        position="relative"
      >
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: mapHeight }}
            center={{ lat: 51.5074, lng: -0.1278 }}
            zoom={10}
            onLoad={(m) => setMap(m)}
            onUnmount={() => setMap(null)}
            options={MAP_OPTIONS}
          >
            {locations.map((loc, i) =>
              loc.location_lat != null && loc.location_lng != null ? (
                <Marker key={i} position={{ lat: loc.location_lat, lng: loc.location_lng }} />
              ) : null
            )}
          </GoogleMap>
        ) : (
          <Box
            h={mapHeight}
            bg="gray.50"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="sm" color="gray.400">Loading map…</Text>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default MultiLocationPicker
